// components/dashboard/BulkEmailPanel.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  IconButton,
  Collapse,
  Avatar,
  ListItemAvatar,
  Switch,
  FormControlLabel,
  Tooltip,
  ListItemSecondaryAction,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { toast } from 'react-toastify';

import logger from '../../utils/logger';

interface Recipient {
  clubId: string;
  clubName: string;
  contactPersonName: string;
  email: string;
  isActive?: boolean;
}

interface AdditionalUser {
  userId: string;
  name: string;
  email: string;
  clubName: string;
  isActive?: boolean;
}

interface BulkEmailResult {
  clubName: string;
  contactPersonName: string;
  email: string;
  type: string;
  status: 'success' | 'error';
  error?: string;
}

interface Competition {
  id: string;
  name: string;
  location: string;
}

interface AttachmentFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  uploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

interface BulkEmailPanelProps {
  competitionId: string;
}

const BulkEmailPanel: React.FC<BulkEmailPanelProps> = ({ competitionId }) => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [additionalUsers, setAdditionalUsers] = useState<AdditionalUser[]>([]);
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showAdditionalUsers, setShowAdditionalUsers] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    body: '',
    senderName: 'Wettkampfmeldung Portal',
    senderEmail: ''
  });
  const [sendResults, setSendResults] = useState<BulkEmailResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  
  // Attachment states
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File upload constraints
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB total
  const MAX_FILES = 5;
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];

  // ... (existing useEffect hooks remain the same)
  useEffect(() => {
    if (competitionId) {
      loadRecipients();
      loadAdditionalUsers();
    }
  }, [competitionId]);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/bulk-emails/competition/${competitionId}/recipients`);
      const recipientsWithActiveState = response.data.recipients.map((recipient: Recipient) => ({
        ...recipient,
        isActive: true
      }));
      setRecipients(recipientsWithActiveState);
      setCompetition(response.data.competition);
      
      setEmailData(prev => ({
        ...prev,
        subject: `Wichtige Informationen zu ${response.data.competition.name}`
      }));
    } catch (error) {
      logger.error('Fehler beim Laden der Empfänger:', {error:error});
      toast.error('Fehler beim Laden der Empfänger');
    } finally {
      setLoading(false);
    }
  };

  const loadAdditionalUsers = async () => {
    try {
      const response = await axios.get(`/api/bulk-emails/competition/${competitionId}/additional-users`);
      const usersWithActiveState = response.data.additionalUsers.map((user: AdditionalUser) => ({
        ...user,
        isActive: false
      }));
      setAdditionalUsers(usersWithActiveState);
    } catch (error) {
      logger.error('Fehler beim Laden der zusätzlichen Benutzer:', {error: error});
      toast.error('Fehler beim Laden der zusätzlichen Benutzer');
    }
  };

  // ... (existing toggle and selection functions remain the same)
  const toggleRecipient = (clubId: string) => {
    setRecipients(prev => prev.map(recipient => 
      recipient.clubId === clubId 
        ? { ...recipient, isActive: !recipient.isActive }
        : recipient
    ));
  };

  const toggleAdditionalUser = (userId: string) => {
    setAdditionalUsers(prev => prev.map(user => 
      user.userId === userId 
        ? { ...user, isActive: !user.isActive }
        : user
    ));
  };

  const selectAllRecipients = () => {
    setRecipients(prev => prev.map(recipient => ({ ...recipient, isActive: true })));
    toast.success('Alle Meldeverantwortlichen aktiviert');
  };

  const deselectAllRecipients = () => {
    setRecipients(prev => prev.map(recipient => ({ ...recipient, isActive: false })));
    toast.info('Alle Meldeverantwortlichen deaktiviert');
  };

  const selectAllAdditionalUsers = () => {
    setAdditionalUsers(prev => prev.map(user => ({ ...user, isActive: true })));
    toast.success('Alle zusätzlichen Empfänger aktiviert');
  };

  const deselectAllAdditionalUsers = () => {
    setAdditionalUsers(prev => prev.map(user => ({ ...user, isActive: false })));
    toast.info('Alle zusätzlichen Empfänger deaktiviert');
  };

  // Attachment handling functions
  const generateFileId = () => Math.random().toString(36).substr(2, 9);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `Datei ist zu groß. Maximum: ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Dateityp nicht erlaubt. Erlaubt: PDF, Word, Excel, Text, Bilder';
    }
    
    return null;
  };

  const getTotalAttachmentSize = () => {
    return attachments.reduce((total, attachment) => total + attachment.size, 0);
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);
    
    // Check file count limit
    if (attachments.length + newFiles.length > MAX_FILES) {
      toast.error(`Maximal ${MAX_FILES} Dateien erlaubt`);
      return;
    }

    // Validate and add files
    const validFiles: AttachmentFile[] = [];
    let totalSize = getTotalAttachmentSize();

    for (const file of newFiles) {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
        continue;
      }

      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        toast.error(`Gesamtgröße würde ${formatFileSize(MAX_TOTAL_SIZE)} überschreiten`);
        break;
      }

      // Check for duplicate names
      if (attachments.some(att => att.name === file.name)) {
        toast.error(`Datei "${file.name}" bereits hinzugefügt`);
        continue;
      }

      validFiles.push({
        id: generateFileId(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploading: false
      });

      totalSize += file.size;
    }

    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} Datei(en) hinzugefügt`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
    toast.info('Datei entfernt');
  };

  const clearAllAttachments = () => {
    setAttachments([]);
    toast.info('Alle Anhänge entfernt');
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // ... (existing calculation variables remain the same)
  const activeRecipients = recipients.filter(r => r.isActive);
  const inactiveRecipients = recipients.filter(r => !r.isActive);
  const activeAdditionalUsers = additionalUsers.filter(u => u.isActive);
  const inactiveAdditionalUsers = additionalUsers.filter(u => !u.isActive);
  const totalActiveEmails = activeRecipients.length + activeAdditionalUsers.length;

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.body.trim()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (totalActiveEmails === 0) {
      toast.error('Mindestens ein Empfänger muss aktiviert sein');
      return;
    }

    setSending(true);
    setSendResults([]);
    setShowResults(false);

    try {
      logger.info('emailData before sending:', emailData);
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add email data
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('senderName', emailData.senderName);
      formData.append('senderEmail', emailData.senderEmail);
      formData.append('activeRecipients', JSON.stringify(activeRecipients));
      formData.append('activeAdditionalUsers', JSON.stringify(activeAdditionalUsers.map(user => ({
        userId: user.userId,
        name: user.name,
        email: user.email,
        clubName: user.clubName
      }))));

      // Add attachments
      attachments.forEach((attachment, index) => {
        formData.append('attachments', attachment.file);
      });

      const response = await axios.post(`/api/bulk-emails/competition/${competitionId}/send`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      setSendResults(response.data.recipients);
      setShowResults(true);
      
      if (response.data.totalErrors === 0) {
        toast.success(`E-Mail erfolgreich an ${response.data.totalSent} Empfänger gesendet`);
        // Clear attachments after successful send
        setAttachments([]);
      } else {
        toast.warning(`${response.data.totalSent} E-Mails gesendet, ${response.data.totalErrors} Fehler`);
      }
    } catch (error) {
      logger.error('Fehler beim E-Mail-Versand:', {error:error});
      toast.error('Fehler beim E-Mail-Versand');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setEmailData({
      subject: competition ? `Wichtige Informationen zu ${competition.name}` : '',
      body: '',
      senderName: 'Wettkampfmeldung Portal',
      senderEmail: ''
    });
    setSendResults([]);
    setShowResults(false);
    setAttachments([]);
  };

  const getPlaceholderText = () => {
    return `Verfügbare Platzhalter:
{competitionName} - ${competition?.name || 'Name des Wettkampfes'}
{competitionLocation} - ${competition?.location || 'Ort des Wettkampfes'}

Beispiel:
Liebe Meldeverantwortliche,

hiermit möchten wir Sie über wichtige Änderungen zum Wettkampf {competitionName} in {competitionLocation} informieren.

Mit freundlichen Grüßen,
Das Organisationsteam`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Lade Empfänger...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon color="primary" />
        E-Mail an Meldeverantwortliche & weitere Empfänger
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Senden Sie eine E-Mail an Meldeverantwortliche und/oder zusätzliche Empfänger.
      </Typography>

      <Grid container spacing={3}>
        {/* Meldeverantwortliche */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <BusinessIcon />
                </Avatar>
              }
              title={
                <Box display="flex" alignItems="center" gap={2}>
                  <span>Meldeverantwortliche ({recipients.length})</span>
                  <Chip 
                    size="small" 
                    label={`${activeRecipients.length} aktiviert`} 
                    color="success"
                    variant="outlined"
                  />
                  {inactiveRecipients.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${inactiveRecipients.length} deaktiviert`} 
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
              }
              subheader="Kontaktpersonen der angemeldeten Vereine"
              action={
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title="Alle aktivieren">
                    <IconButton onClick={selectAllRecipients} size="small">
                      <SelectAllIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Alle deaktivieren">
                    <IconButton onClick={deselectAllRecipients} size="small">
                      <DeselectIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => setShowRecipients(!showRecipients)}>
                    {showRecipients ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              }
            />
            <Collapse in={showRecipients}>
              <CardContent sx={{ pt: 0 }}>
                <List dense>
                  {recipients.map((recipient, index) => (
                    <React.Fragment key={recipient.clubId}>
                      <ListItem
                        sx={{
                          opacity: recipient.isActive ? 1 : 0.5,
                          backgroundColor: recipient.isActive ? 'transparent' : 'action.hover'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            bgcolor: recipient.isActive ? 'secondary.main' : 'grey.400' 
                          }}>
                            <BusinessIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={recipient.contactPersonName}
                          secondary={`${recipient.clubName} - ${recipient.email}`}
                        />
                        <ListItemSecondaryAction>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={recipient.isActive || false}
                                onChange={() => toggleRecipient(recipient.clubId)}
                                size="small"
                              />
                            }
                            label={recipient.isActive ? "Aktiv" : "Inaktiv"}
                            labelPlacement="start"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < recipients.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Collapse>
          </Card>
        </Grid>

        {/* Zusätzliche Empfänger */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={
                <Badge badgeContent={activeAdditionalUsers.length} color="primary">
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <PersonAddIcon />
                  </Avatar>
                </Badge>
              }
              title={
                <Box display="flex" alignItems="center" gap={2}>
                  <span>Weitere Empfänger ({additionalUsers.length})</span>
                  <Chip 
                    size="small" 
                    label={`${activeAdditionalUsers.length} aktiviert`} 
                    color="success"
                    variant="outlined"
                  />
                  {inactiveAdditionalUsers.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${inactiveAdditionalUsers.length} deaktiviert`} 
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
              }
              subheader="Zusätzliche Benutzer (ohne Admin/Organizer/Helper)"
              action={
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title="Alle aktivieren">
                    <IconButton onClick={selectAllAdditionalUsers} size="small">
                      <SelectAllIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Alle deaktivieren">
                    <IconButton onClick={deselectAllAdditionalUsers} size="small">
                      <DeselectIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => setShowAdditionalUsers(!showAdditionalUsers)}>
                    {showAdditionalUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              }
            />
            <Collapse in={showAdditionalUsers}>
              <CardContent sx={{ pt: 0 }}>
                {additionalUsers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    Keine weiteren Benutzer verfügbar
                  </Typography>
                ) : (
                  <List dense>
                    {additionalUsers.map((user, index) => (
                      <React.Fragment key={user.userId}>
                        <ListItem
                          sx={{
                            opacity: user.isActive ? 1 : 0.5,
                            backgroundColor: user.isActive ? 'transparent' : 'action.hover'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ 
                              bgcolor: user.isActive ? 'info.main' : 'grey.400' 
                            }}>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={user.name}
                            secondary={`${user.clubName} - ${user.email}`}
                          />
                          <ListItemSecondaryAction>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={user.isActive || false}
                                  onChange={() => toggleAdditionalUser(user.userId)}
                                  size="small"
                                />
                              }
                              label={user.isActive ? "Aktiv" : "Inaktiv"}
                              labelPlacement="start"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < additionalUsers.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>

        {/* E-Mail-Formular */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="E-Mail verfassen"
              subheader={`Wird an ${totalActiveEmails} aktivierte Empfänger gesendet (alle als BCC)`}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Absender Name (optional)"
                    value={emailData.senderName}
                    onChange={handleInputChange('senderName')}
                    variant="outlined"
                    helperText="Leer lassen für Standard-Absender"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Absender E-Mail (optional)"
                    type="email"
                    value={emailData.senderEmail}
                    onChange={handleInputChange('senderEmail')}
                    variant="outlined"
                    helperText="Leer lassen für Standard-Absender"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Betreff"
                    value={emailData.subject}
                    onChange={handleInputChange('subject')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Nachricht"
                    multiline
                    rows={10}
                    value={emailData.body}
                    onChange={handleInputChange('body')}
                    variant="outlined"
                    placeholder={getPlaceholderText()}
                  />
                </Grid>

                {/* Attachment Section */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardHeader
                      title={
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachFileIcon />
                          <span>Anhänge</span>
                          {attachments.length > 0 && (
                            <Chip 
                              size="small" 
                              label={`${attachments.length} Datei(en)`} 
                              color="primary"
                            />
                          )}
                        </Box>
                      }
                      subheader={`Max. ${MAX_FILES} Dateien, je max. ${formatFileSize(MAX_FILE_SIZE)}, gesamt max. ${formatFileSize(MAX_TOTAL_SIZE)}`}
                      action={
                        <Box display="flex" gap={1}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={(e) => handleFileSelect(e.target.files)}
                            style={{ display: 'none' }}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                          />
                          <Button
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={attachments.length >= MAX_FILES}
                          >
                            Dateien wählen
                          </Button>
                          {attachments.length > 0 && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={clearAllAttachments}
                            >
                              Alle entfernen
                            </Button>
                          )}
                        </Box>
                      }
                    />
                    <CardContent sx={{ pt: 0 }}>
                      {/* Drag & Drop Area */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          border: dragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                          backgroundColor: dragOver ? 'action.hover' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="h6" color="text.secondary">
                          Dateien hier ablegen oder klicken zum Auswählen
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          PDF, Word, Excel, Text, Bilder
                        </Typography>
                      </Paper>

                      {/* Attachment List */}
                      {attachments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Ausgewählte Anhänge ({formatFileSize(getTotalAttachmentSize())} gesamt):
                          </Typography>
                          <List dense>
                            {attachments.map((attachment) => (
                              <ListItem
                                key={attachment.id}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar>
                                    <FileIcon />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={attachment.name}
                                  secondary={`${formatFileSize(attachment.size)} • ${attachment.type}`}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    onClick={() => removeAttachment(attachment.id)}
                                    size="small"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Size Warning */}
                      {getTotalAttachmentSize() > MAX_TOTAL_SIZE * 0.8 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <WarningIcon />
                            Sie nähern sich der maximalen Anhangsgröße ({formatFileSize(MAX_TOTAL_SIZE)})
                          </Box>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Platzhalter-Hinweis */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Sie können Platzhalter verwenden: <code>{'{competitionName}'}</code>, <code>{'{competitionLocation}'}</code>
                </Typography>
              </Alert>

              {/* Status-Übersicht */}
              <Paper elevation={1} sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Empfänger-Übersicht:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip 
                    icon={<BusinessIcon />}
                    label={`${activeRecipients.length} Meldeverantwortliche`} 
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip 
                    icon={<PersonIcon />}
                    label={`${activeAdditionalUsers.length} weitere Empfänger`} 
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Chip 
                    icon={<GroupIcon />}
                    label={`${totalActiveEmails} gesamt aktiv`} 
                    color="info"
                    variant="filled"
                    size="small"
                  />
                  {attachments.length > 0 && (
                    <Chip 
                      icon={<AttachFileIcon />}
                      label={`${attachments.length} Anhang/Anhänge (${formatFileSize(getTotalAttachmentSize())})`} 
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>

              {/* Warnungen */}
              {(inactiveRecipients.length > 0 || inactiveAdditionalUsers.length > 0) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {inactiveRecipients.length + inactiveAdditionalUsers.length} Empfänger sind deaktiviert
                    </strong> und erhalten keine E-Mail.
                    {inactiveRecipients.length > 0 && ` ${inactiveRecipients.length} Meldeverantwortliche`}
                    {inactiveRecipients.length > 0 && inactiveAdditionalUsers.length > 0 && ', '}
                    {inactiveAdditionalUsers.length > 0 && ` ${inactiveAdditionalUsers.length} weitere Empfänger`}
                  </Typography>
                </Alert>
              )}

              {/* Aktionen */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={sending}
                >
                  Zurücksetzen
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendEmail}
                  disabled={sending || totalActiveEmails === 0 || !emailData.subject.trim() || !emailData.body.trim()}
                  startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {sending ? 'Sende...' : `E-Mail senden (${totalActiveEmails})`}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Versand-Ergebnisse */}
        {showResults && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Versand-Ergebnisse"
                subheader="Übersicht über den E-Mail-Versand"
              />
              <CardContent>
                <Box display="flex" gap={1} mb={2}>
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label={`${sendResults.filter(r => r.status === 'success').length} erfolgreich`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip 
                    icon={<ErrorIcon />}
                    label={`${sendResults.filter(r => r.status === 'error').length} Fehler`}
                    color="error"
                    variant="outlined"
                  />
                </Box>
                
                {/* Gruppierte Ergebnisse */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Meldeverantwortliche:
                  </Typography>
                  <List dense>
                    {sendResults.filter(r => r.type === 'meldeverantwortlich').map((result, index) => (
                      <ListItem key={`mel-${index}`}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: result.status === 'success' ? 'success.main' : 'error.main' }}>
                            {result.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${result.contactPersonName} (${result.clubName})`}
                          secondary={result.status === 'error' ? result.error : result.email}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {sendResults.some(r => r.type === 'zusaetzlich') && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Weitere Empfänger:
                      </Typography>
                      <List dense>
                        {sendResults.filter(r => r.type === 'zusaetzlich').map((result, index) => (
                          <ListItem key={`add-${index}`}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: result.status === 'success' ? 'success.main' : 'error.main' }}>
                                {result.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${result.contactPersonName} (${result.clubName})`}
                              secondary={result.status === 'error' ? result.error : result.email}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BulkEmailPanel;